import React, { forwardRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import * as Yup from "yup";

import { TextField } from "@material-ui/core";
import { ButtonsWrapper, StyledForm } from "./styled";

import { getRequiredField } from "helpers/formValidations";
import { CAPTURES_CONFIG, CAPTURES_PHRASE } from "./config";

import RegularButton from "components/CustomButtons/Button";
import { fetchEditCapture } from "redux/actions/buildings/captures/capturesActions";

const CapturesSchema = Yup.object({
  [CAPTURES_CONFIG.captureDate.key]: Yup.date("")
    .typeError(getRequiredField(CAPTURES_CONFIG.captureDate.label))
    .required(getRequiredField(CAPTURES_CONFIG.captureDate.label)),

  [CAPTURES_CONFIG.capture.key]: Yup.string()
    .required(getRequiredField(CAPTURES_CONFIG.capture.label))
    .matches(
      /^\d*\.?\d{0,3}$/,
      `${CAPTURES_CONFIG.capture.label} debe tener un máximo de 3 decimales.`
    )
});

export const CapturesForm = forwardRef(props => {
  const dispatch = useDispatch();
  const { handleClose } = props;
  const {
    isAddMode,
    deleteMode,
    editCapture,
    routeCycleSelected
  } = useSelector(state => state.captures);

  const handleCloseForm = () => {
    handleClose();
    reset();
  };

  const {
    reset,
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(CapturesSchema)
  });

  const onSubmit = React.useCallback(
    formData => {
      // You can use the validated data to make your API request here
      const body = {
        captureDate: dayjs(formData[CAPTURES_CONFIG.captureDate.key]).format(
          "YYYY-MM-DD"
        ),
        capture: Number(formData[CAPTURES_CONFIG.capture.key])
      };
      if (!isAddMode) {
        dispatch(fetchEditCapture(body, editCapture.id, routeCycleSelected.id));
      }

      handleCloseForm();
    },
    [reset]
  );

  const parseSupportType = (type) => {
    switch (type) {
      case 'MALFUNCTION':
        return 'Dejó de funcionar el medidor'
      case 'MISSING':
        return 'No se encuentra el medidor'
      case 'REPLACED':
        return 'Cambio de medidor'
      case 'VANDALIZED':
        return 'Medidor vandalizado'
      default:
        return type
    }
  }

  useEffect(() => {
    if (editCapture) {
      const editDate = dayjs(
        editCapture[CAPTURES_CONFIG.captureDate.key]
      ).format("YYYY-MM-DD");

      setValue(CAPTURES_CONFIG.captureDate.key, editDate);
      setValue(
        CAPTURES_CONFIG.capture.key,
        editCapture[CAPTURES_CONFIG.capture.key]
      );
      setValue(CAPTURES_CONFIG.motive.key, editCapture[CAPTURES_CONFIG.motive.key])
      setValue(CAPTURES_CONFIG.help.key,
        parseSupportType(editCapture[CAPTURES_CONFIG.help.key])
      )
      setValue(CAPTURES_CONFIG.notes.key, editCapture[CAPTURES_CONFIG.notes.key])
    }
  }, [editCapture]);

  useEffect(() => {
    if (isAddMode) {
      reset();
    }
  }, [isAddMode]);

  return (
    <>
      <StyledForm component="form" fullWidth onSubmit={handleSubmit(onSubmit)}>
        <TextField
          fullWidth
          type="date"
          label={CAPTURES_CONFIG.captureDate.label}
          variant="outlined"
          error={!!errors[CAPTURES_CONFIG.captureDate.key]}
          helperText={errors[CAPTURES_CONFIG.captureDate.key]?.message}
          {...register(CAPTURES_CONFIG.captureDate.key)}
          InputLabelProps={{
            shrink: true
          }}
          size="small"
        />

        <TextField
          fullWidth
          type="number"
          label={CAPTURES_CONFIG.capture.label}
          variant="outlined"
          error={!!errors[CAPTURES_CONFIG.capture.key]}
          helperText={errors[CAPTURES_CONFIG.capture.key]?.message}
          {...register(CAPTURES_CONFIG.capture.key)}
          InputLabelProps={{
            shrink: true
          }}
          inputProps={{
            step: "0.001" // Permite hasta 3 decimales
          }}
          size="small"
        />

        {editCapture[CAPTURES_CONFIG.notes.key] ?
          <TextField
            fullWidth
            disabled={true}
            type="text"
            label={CAPTURES_CONFIG.notes.label}
            variant="outlined"
            error={!!errors[CAPTURES_CONFIG.notes.key]}
            helperText={errors[CAPTURES_CONFIG.notes.key]?.message}
            {...register(CAPTURES_CONFIG.notes.key)}
            InputLabelProps={{
              shrink: true,
            }}
            size="small"
          />
          : null}

          {editCapture[CAPTURES_CONFIG.motive.key] ?
            <TextField
              fullWidth
              disabled={true}
              type="text"
              label={CAPTURES_CONFIG.motive.label}
              variant="outlined"
              error={!!errors[CAPTURES_CONFIG.motive.key]}
              helperText={errors[CAPTURES_CONFIG.motive.key]?.message}
              {...register(CAPTURES_CONFIG.motive.key)}
              InputLabelProps={{
                shrink: true,
              }}
              size="small"
            />
            : null}

        {editCapture[CAPTURES_CONFIG.help.key] ?
          <TextField
            fullWidth
            disabled={true}
            type="text"
            label={CAPTURES_CONFIG.help.label}
            variant="outlined"
            error={!!errors[CAPTURES_CONFIG.help.key]}
            helperText={errors[CAPTURES_CONFIG.help.key]?.message}
            {...register(CAPTURES_CONFIG.help.key)}
            InputLabelProps={{
              shrink: true,
            }}
            size="small"
          />
          : null}

        <ButtonsWrapper>
          <RegularButton
            variant="contained"
            color="danger"
            onClick={handleClose}
          >
            {CAPTURES_PHRASE.modal.actions.cancel}
          </RegularButton>
          <RegularButton
            variant="contained"
            type="submit"
            color="info"
          >
            {isAddMode
              ? CAPTURES_PHRASE.modal.actions.add
              : deleteMode
                ? CAPTURES_PHRASE.modal.actions.delete
                : CAPTURES_PHRASE.modal.actions.save}
          </RegularButton>
        </ButtonsWrapper>
      </StyledForm>
    </>
  );
});
